import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService, AuthTokens } from '../services/authService';

export interface AuthState {
  accessToken: string | null;
  idToken: string | null;
  refreshToken: string | null;
  tokenExpiresAt: number | null;
  isAuthenticated: boolean;
  username: string | null;
  mfaSetupSession: string | null; // Store session for MFA setup flow
  
  // Auth actions
  login: (username: string, password: string) => Promise<{ requiresMFA?: boolean; requiresNewPassword?: boolean; requiresMFASetup?: boolean; session?: string }>;
  verifyMFA: (username: string, code: string, session: string) => Promise<void>;
  setNewPassword: (username: string, newPassword: string, session: string) => Promise<{ requiresMFASetup?: boolean; session?: string }>;
  setupMFAWithSession: (session: string) => Promise<{ secretCode: string; qrCodeUrl: string; session: string }>;
  verifyMFASetupWithSession: (username: string, code: string, session: string) => Promise<void>;
  setupMFA: () => Promise<{ secretCode: string; qrCodeUrl: string }>;
  verifyMFASetup: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshTokens: () => Promise<void>;
  checkTokenExpiry: () => boolean;
  ensureValidToken: () => Promise<string | null>;
  
  // Internal state setters
  setTokens: (tokens: AuthTokens, username: string) => void;
  setUsername: (username: string) => void;
  setMFASetupSession: (session: string | null) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      idToken: null,
      refreshToken: null,
      tokenExpiresAt: null,
      isAuthenticated: false,
      username: null,
      mfaSetupSession: null,

      setTokens: (tokens: AuthTokens, username: string) => {
        const expiresAt = Date.now() + tokens.expiresIn * 1000;
        set({
          accessToken: tokens.accessToken,
          idToken: tokens.idToken,
          refreshToken: tokens.refreshToken,
          tokenExpiresAt: expiresAt,
          isAuthenticated: true,
          username,
          mfaSetupSession: null,
        });
      },

      setUsername: (username: string) => {
        set({ username });
      },

      setMFASetupSession: (session: string | null) => {
        set({ mfaSetupSession: session });
      },

      login: async (username: string, password: string) => {
        try {
          const response = await authService.signIn(username, password);
          
          // Store username for later use
          set({ username });

          if (response.challengeName === 'SOFTWARE_TOKEN_MFA') {
            return { requiresMFA: true, session: response.session };
          }

          if (response.challengeName === 'NEW_PASSWORD_REQUIRED') {
            return { requiresNewPassword: true, session: response.session };
          }

          if (response.challengeName === 'MFA_SETUP') {
            // User needs to set up MFA
            if (response.tokens) {
              get().setTokens(response.tokens, username);
            }
            return { requiresMFA: false };
          }

          if (response.tokens) {
            get().setTokens(response.tokens, username);
            return {};
          }

          throw new Error('Unexpected authentication response');
        } catch (error: any) {
          console.error('Login error:', error);
          throw error;
        }
      },

      verifyMFA: async (username: string, code: string, session: string) => {
        try {
          const tokens = await authService.respondToMFAChallenge(username, code, session);
          get().setTokens(tokens, username);
        } catch (error: any) {
          console.error('MFA verification error:', error);
          throw error;
        }
      },

      setNewPassword: async (username: string, newPassword: string, session: string) => {
        try {
          const response = await authService.completeNewPasswordChallenge(username, newPassword, session);
          
          if (response.challengeName === 'MFA_SETUP') {
            // User needs to set up MFA after password change
            // Store the session for MFA setup
            get().setMFASetupSession(response.session || '');
            return { requiresMFASetup: true, session: response.session };
          }

          if (response.tokens) {
            get().setTokens(response.tokens, username);
            return {};
          }

          throw new Error('Unexpected response after password change');
        } catch (error: any) {
          console.error('Set new password error:', error);
          throw error;
        }
      },

      setupMFAWithSession: async (session: string) => {
        try {
          const state = get();
          const mfaSetup = await authService.setupMFA(session, true, state.username || undefined);
          // Store the new session returned from AssociateSoftwareToken
          get().setMFASetupSession(mfaSetup.session);
          return {
            secretCode: mfaSetup.secretCode,
            qrCodeUrl: mfaSetup.qrCodeUrl,
            session: mfaSetup.session,
          };
        } catch (error: any) {
          console.error('MFA setup error:', error);
          throw error;
        }
      },

      verifyMFASetupWithSession: async (username: string, code: string, session: string) => {
        try {
          const tokens = await authService.verifyMFASetupAndAuthenticate(username, code, session);
          get().setTokens(tokens, username);
        } catch (error: any) {
          console.error('MFA verification error:', error);
          throw error;
        }
      },

      setupMFA: async () => {
        const state = get();
        if (!state.accessToken) {
          throw new Error('Not authenticated');
        }

        try {
          const mfaSetup = await authService.setupMFA(state.accessToken, false, state.username || undefined);
          return {
            secretCode: mfaSetup.secretCode,
            qrCodeUrl: mfaSetup.qrCodeUrl,
          };
        } catch (error: any) {
          console.error('MFA setup error:', error);
          throw error;
        }
      },

      verifyMFASetup: async (code: string) => {
        const state = get();
        if (!state.accessToken) {
          throw new Error('Not authenticated');
        }

        try {
          await authService.verifyMFASetup(state.accessToken, code);
        } catch (error: any) {
          console.error('MFA verification error:', error);
          throw error;
        }
      },

      logout: async () => {
        const state = get();
        if (state.accessToken) {
          try {
            await authService.signOut(state.accessToken);
          } catch (error) {
            console.error('Sign out error:', error);
          }
        }

        set({
          accessToken: null,
          idToken: null,
          refreshToken: null,
          tokenExpiresAt: null,
          isAuthenticated: false,
          username: null,
        });
      },

      refreshTokens: async () => {
        const state = get();
        if (!state.refreshToken) {
          throw new Error('No refresh token available');
        }

        try {
          const tokens = await authService.refreshToken(state.refreshToken);
          get().setTokens(tokens, state.username || '');
        } catch (error: any) {
          console.error('Token refresh error:', error);
          // If refresh fails, log out
          await get().logout();
          throw error;
        }
      },

      checkTokenExpiry: () => {
        const state = get();
        if (!state.accessToken || !state.tokenExpiresAt) {
          return false;
        }
        
        const now = Date.now();
        const bufferTime = 5 * 60 * 1000; // 5 minutes buffer
        
        if (now >= state.tokenExpiresAt) {
          // Token is fully expired
          if (state.refreshToken) {
            get().refreshTokens().catch(() => {
              // Refresh failed, log out
              get().logout();
            });
            // Still considered valid while refresh is in-flight
            return true;
          }
          return false;
        }
        
        if (now >= state.tokenExpiresAt - bufferTime) {
          // Token is about to expire, proactively refresh
          if (state.refreshToken) {
            get().refreshTokens().catch(() => {
              // Refresh failed, will retry on next check
            });
          }
          // Token is still valid, don't log out
          return true;
        }
        
        return true;
      },

      /**
       * Ensure the token is valid before making an API request.
       * If the token is expired or about to expire, refreshes it first.
       * Returns the current valid idToken, or null if refresh fails.
       */
      ensureValidToken: async (): Promise<string | null> => {
        const state = get();
        if (!state.idToken || !state.tokenExpiresAt) {
          return null;
        }

        const now = Date.now();
        const bufferTime = 5 * 60 * 1000; // 5 minutes buffer

        if (now >= state.tokenExpiresAt - bufferTime) {
          // Token expired or about to expire — refresh before returning
          if (state.refreshToken) {
            try {
              await get().refreshTokens();
              return get().idToken;
            } catch {
              return null;
            }
          }
          return null;
        }

        return state.idToken;
      },
    }),
    {
      name: 'auth-storage',
    }
  )
);

